import { useEffect, useState, } from 'react';
import { getEventInputType, } from './getEventInputType';
/**
 * Utility to simplify the "has passed swipe distance" check.
 *
 * When multiplied to a delta, will zero out the unwanted direction
 * and invert directions that need to be to distance as a vector
 */
const DIR_TO_MULT = {
    down: {
        x: 0,
        y: 1,
    },
    left: {
        x: -1,
        y: 0,
    },
    right: {
        x: 1,
        y: 0,
    },
    up: {
        x: 0,
        y: -1,
    },
};
export const useSwipe = ({ onSwipeEnd, swipeDistance = 30, allowedDirections = [
    'up',
    'down',
    'left',
    'right',
], }) => {
    const [startPosition, setStartPosition,] = useState({
        x: 0,
        y: 0,
    });
    const [activeEventType, setActiveEventType,] = useState(null);
    const [dragDelta, setDragDelta,] = useState({
        x: 0,
        y: 0,
    });
    const getPosition = (event, eventType) => {
        const evType = eventType || getEventInputType(event);
        // Touch
        if (evType === 'touch') {
            const evAsTouch = event;
            // onTouchEnd doesn't provide a touch
            if (!evAsTouch.targetTouches.length) {
                return null;
            }
            return {
                x: evAsTouch.targetTouches[0].clientX,
                y: evAsTouch.targetTouches[0].clientY,
            };
        }
        // Mouse
        if (evType === 'mouse') {
            const evAsMouse = event;
            return {
                x: evAsMouse.clientX,
                y: evAsMouse.clientY,
            };
        }
        return null;
    };
    const getDelta = (newPos) => ({
        x: newPos.x - startPosition.x,
        y: newPos.y - startPosition.y,
    });
    const handleSwipeMove = (event) => {
        const newPos = getPosition(event);
        if (newPos) {
            setDragDelta(getDelta(newPos));
        }
    };
    const getPassingDirection = (delta) => {
        // Using a for loop to break early
        /* eslint-disable-next-line id-length */
        for (let i = 0; i < allowedDirections.length; i += 1) {
            const dir = allowedDirections[i];
            const deltaMult = DIR_TO_MULT[dir];
            if (delta.x * deltaMult.x > swipeDistance ||
                delta.y * deltaMult.y > swipeDistance) {
                return dir;
            }
        }
        return null;
    };
    const resetState = () => {
        setActiveEventType(null);
        setDragDelta({
            x: 0,
            y: 0,
        });
    };
    const handleSwipeEnd = (event) => {
        const endPos = getPosition(event);
        const endDelta = endPos ? getDelta(endPos) : dragDelta;
        const passingDir = getPassingDirection(endDelta);
        // Reset the activeEventType and dragDelta
        resetState();
        window.requestAnimationFrame(() => {
            // Trigger the callback
            onSwipeEnd(passingDir);
        });
    };
    const handleStartSwipe = ({ nativeEvent, }) => {
        const eventType = getEventInputType(nativeEvent);
        if (eventType === 'other') {
            return;
        }
        // Do nothing on right click
        if (eventType === 'mouse' && nativeEvent.button === 2) {
            return;
        }
        const pos = getPosition(nativeEvent);
        if (pos) {
            setStartPosition(pos);
            setActiveEventType(eventType);
        }
    };
    // If a user right clicks while swiping, cancel the current swipe
    const handleRightClick = (event) => {
        if (event.button === 2) {
            handleSwipeEnd(event);
        }
    };
    /**
     * `onScroll` listener for any scrollable child components.
     * Cancels the swipe action as soon as a scroll is triggered.
     *
     * When a user triggers a swipe after an child's content has
     * been scrolled to an extremity, no `onScroll` is triggered
     * and the normal swipe will be handled.
     *
     * Note that `overscroll-behavior: none;` should be applied
     * to the scrollable child to prevent inertial scrolling
     * which will trigger `onScroll` even if scrolled to the edge.
     */
    const handleScrollableChild = () => {
        resetState();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (activeEventType === 'touch') {
            // Touch
            window.addEventListener('touchmove', handleSwipeMove);
        }
        else if (activeEventType === 'mouse') {
            // Mouse
            window.addEventListener('mousemove', handleSwipeMove);
            window.addEventListener('mousedown', handleRightClick);
        }
        return () => {
            // Touch
            window.removeEventListener('touchmove', handleSwipeMove);
            // Mouse
            window.removeEventListener('mousemove', handleSwipeMove);
            window.removeEventListener('mousedown', handleRightClick);
        };
    }, [
        activeEventType,
    ]);
    useEffect(() => {
        if (activeEventType === 'touch') {
            // Touch
            window.addEventListener('touchend', handleSwipeEnd);
        }
        else if (activeEventType === 'mouse') {
            // Mouse
            window.addEventListener('mouseup', handleSwipeEnd);
        }
        return () => {
            // Touch
            window.removeEventListener('touchend', handleSwipeEnd);
            // Mouse
            window.removeEventListener('mouseup', handleSwipeEnd);
        };
    }, [
        dragDelta,
        activeEventType,
    ]);
    /* eslint-enable react-hooks/exhaustive-deps */
    return {
        dragDelta,
        handleScrollableChild,
        handleStartSwipe,
        isSwiping: activeEventType !== null,
    };
};
